import { useRef, useState } from 'react';
import { clearLogin, setLogin } from '@/features/loginSlice';
import { getHeaders, prepareApiHeaders } from '@/features/utils';
import { resetClientScopedApiState } from '@/features/resetClientApiState';
import { getAppConfig } from '@/app/config';
import { useAppDispatch } from '@/app/hooks';
import { captureException, setUser as setSentryUser } from '@sentry/react';
import { useIntercom } from 'react-use-intercom';
import {
  ID_TOKEN_SESSION_KEY,
  USER_EMAIL,
  METADATA_SESSION_KEY,
  EMAIL,
} from '@/app/constants';
import { ClientContextNotFoundError } from '@/features/types';
import type { ClientDetail } from '@/generated/types/navigator/types.gen';
import type { ClientContext } from '@/app/clientContext';
import { parseDeliveryIdentifier } from '@/hooks/useDeliveryIdentifier';
import {
  getDefaultContext,
  getClientContext,
  userClientContext,
  getLoginEmail,
  fetchUser,
  loadClientMetadata,
  useSentryContextUpdate,
} from '@/app/clientContext';

export function ClientContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const dispatch = useAppDispatch();
  const [token, setToken] = useState<string>(
    sessionStorage.getItem(ID_TOKEN_SESSION_KEY) ?? ''
  );
  const [apiIdentity, setApiIdentity] = useState<string>(
    sessionStorage.getItem(USER_EMAIL) ?? ''
  );
  const [isInternalUser, setIsInternalUser] = useState<boolean>(false);
  const [canClientSwitch, setCanClientSwitch] = useState<boolean>(false);
  const [isUserLoaded, setIsUserLoaded] = useState<boolean>(false);
  const { tagManagerId, environment } = getAppConfig();
  const { update: updateIntercom } = useIntercom();

  // Adding this to get static email used in login since user changes as we change the client for internal users. Email is per login.
  const [email, setEmail] = useState<string>(
    sessionStorage.getItem(EMAIL) ?? ''
  );
  const [context, setContext] = useState<ClientContext>(getDefaultContext());
  // The identity the RTK Query caches were last populated for. Client-scoped
  // data rides only in the request header, never in a cache key, so a switch
  // must explicitly clear those caches or the previous client's data is served.
  const previousApiIdentity = useRef<string>('');
  const setClientMetadata = (data: ClientDetail) => {
    sessionStorage.setItem(METADATA_SESSION_KEY, JSON.stringify(data));
    setContext(getClientContext(data));
  };

  useSentryContextUpdate(email, context.code);

  const refreshClientMetadata = async () => {
    const headers = prepareApiHeaders(new Headers(), {
      getState: () => ({
        login: {
          tokenId: token,
          user: apiIdentity,
        },
      }),
    });

    try {
      const userDetail = await fetchUser(headers);
      setIsInternalUser(userDetail.is_superuser);
      setCanClientSwitch(userDetail.can_client_switch);
      setIsUserLoaded(true);
      const loginEmail = getLoginEmail(email, environment);

      await loadClientMetadata(
        headers,
        loginEmail,
        userDetail,
        environment,
        tagManagerId,
        updateIntercom,
        setClientMetadata
      );
    } catch (error) {
      // A dropped or aborted connection during this background refresh is
      // transient; the next navigation refreshes again.
      if (error instanceof TypeError) return;
      captureException(error);
    }
  };

  const hydrateUserAndClientContext = async (
    token: string,
    apiIdentity: string,
    email: string,
    internalClientCodeHint?: string
  ) => {
    setToken('RANDOM BLAH'); // TODO: delete this, token should not be used anywhere
    setEmail(email);

    try {
      const initialHeaders = getHeaders(token, apiIdentity);
      const userDetail = await fetchUser(initialHeaders);

      let resolvedApiIdentity = apiIdentity;
      if (userDetail.is_superuser && internalClientCodeHint) {
        resolvedApiIdentity = `internal@${internalClientCodeHint}`;
      } else if (
        userDetail.is_superuser &&
        !resolvedApiIdentity.startsWith('internal')
      ) {
        resolvedApiIdentity =
          sessionStorage.getItem(USER_EMAIL) ?? resolvedApiIdentity;
      }
      if (!resolvedApiIdentity.startsWith('internal')) {
        setSentryUser({ email: resolvedApiIdentity });
      }

      setIsInternalUser(userDetail.is_superuser);
      setCanClientSwitch(userDetail.can_client_switch);

      const headers = getHeaders(token, resolvedApiIdentity);
      const loginEmail = getLoginEmail(email, environment);

      const clientDetail = await loadClientMetadata(
        headers,
        loginEmail,
        userDetail,
        environment,
        tagManagerId,
        updateIntercom,
        setClientMetadata
      );

      // A delivery names its own client, which the backend resolves from the
      // `?delivery=` param on each request it rides along with. Bind the
      // identity to the client it resolved to, so requests that no longer carry
      // the param still target that client rather than whichever one was
      // impersonated before. The backend ignores the param for users who cannot
      // switch clients, so for them the resolved code is just their own.
      let finalApiIdentity = resolvedApiIdentity;
      if (
        userDetail.can_client_switch &&
        clientDetail.code &&
        parseDeliveryIdentifier(window.location.search)
      ) {
        finalApiIdentity = `internal@${clientDetail.code}`;
      }

      // Commit the identity only after the metadata fetch proves it resolves
      // to a real client. Committing earlier persists an unresolvable
      // identity to sessionStorage, which every later hydration then reuses
      // and re-fails on — locking the user out of the session.
      setApiIdentity(finalApiIdentity);
      dispatch(setLogin({ token, user: finalApiIdentity, email }));

      // Switching the impersonated client only changes the request header, not
      // any RTK Query cache key, so clear the client-scoped caches to force a
      // refetch under the new identity. Skip the first hydrate (no prior client)
      // and same-client refreshes (header unchanged) to avoid spurious refetches.
      if (
        previousApiIdentity.current &&
        previousApiIdentity.current !== finalApiIdentity
      ) {
        resetClientScopedApiState(dispatch);
      }
      previousApiIdentity.current = finalApiIdentity;

      // Promote isUserLoaded after metadata is in hand so a concurrent
      // hydrate triggered by isUserLoaded flipping cannot race this one's
      // loadClientMetadata and clobber the new client.
      setIsUserLoaded(true);
    } catch (error) {
      // An unresolvable client/delivery/release in the URL is a bad link, not
      // an app failure; RequireAuth handles it by routing to the 404 page.
      if (!(error instanceof ClientContextNotFoundError)) {
        captureException(error);
      }
      throw error;
    }
  };

  const signout = () => {
    setToken('');
    setApiIdentity('');
    setEmail('');
    setIsInternalUser(false);
    setCanClientSwitch(false);
    setIsUserLoaded(false);
    dispatch(clearLogin());
    setContext(getDefaultContext());
  };

  const actualLoginEmail = getLoginEmail(email, environment);

  const auth = {
    token,
    apiIdentity,
    isInternalUser,
    canClientSwitch,
    isUserLoaded,
    email,
    actualLoginEmail,
    context,
    hydrateUserAndClientContext,
    signout,
    refreshClientMetadata,
  };

  return (
    <userClientContext.Provider value={auth}>
      {children}
    </userClientContext.Provider>
  );
}
